import React, { useEffect, useState } from "react";
import {
  ChangeStockItemModel,
  ProductAvailabilityCode,
  productAvailability,
} from "../../../customer/inventory/models/models";
import {
  Box,
  Button,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  ThemeProvider,
  Toolbar,
  createTheme,
} from "@mui/material";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { CurrencyCode, ProductConditionCode, productCondition } from "../../../common/models/CommonModels";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import { FileModel, enumToArray } from "../../../shared/models/models";
import { integerRegex, threeDecimalsRegex, twoDecimalsRegex } from "../../../shared/validators";
import UploadFile from "../../../shared/components/UploadFile";
import Loader from "../../../shared/components/Loader";
import { AdminChangeStockItemModel, AdminInventoryState } from "../../models/AdminStockItemsModels";
import { changeStockItem, getCategories, getManufacturers, getStockItem } from "../../adminStockItemsSlice";
import useTranslation from "../../../shared/hooks/useTranslation";

const EditInventory = () => {
  const { stockItem, manufacturers, categories, loading, success, error } = useAppSelector<AdminInventoryState>(
    (state: any) => state.adminInventory
  );
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AdminChangeStockItemModel>();

  const { id } = useParams();

  const [file, setFile] = useState<FileModel | null>(stockItem?.file || null);

  const handlerCreateFile = (file: FileModel | null) => {
    setFile(file);
  };

  const onSubmit: SubmitHandler<ChangeStockItemModel> = (data: AdminChangeStockItemModel) => {
    data.manufacturerName = manufacturers.find((x) => x.id === data.manufacturerId)?.name || "";
    data.alternativeNumber = data.alternativeNumber || undefined;
    data.conditionCode = Number(data.conditionCode);
    data.availabilityCode = Number(data.availabilityCode);
    data.price = data.price ? { id: CurrencyCode.eur, value: Number(data.price) } : undefined;
    data.quantity = {
      value: data.quantity ? Number(data.quantity.value) : undefined,
      unitCode: Number(data.quantity.unitCode)
    };
    data.height = data.height ? Number(data.height) : undefined;
    data.width = data.width ? Number(data.width) : undefined;
    data.depth = data.depth ? Number(data.depth) : undefined;
    data.weight = data.weight ? Number(data.weight) : undefined;
    data.file = file || undefined;

    handleSubmitStockItem(data);
  };

  const handleSubmitStockItem = async (model: AdminChangeStockItemModel) => {
    await dispatch(changeStockItem({ id: id, model: model }));
    if (error) {
      return;
    }

    navigate("/admin/inventory");
  };

  const handleCancel = () => {
    navigate("/admin/inventory");
  };

  const init = async () => {
    dispatch(getManufacturers());
    dispatch(getCategories());

    if (id) {
      await dispatch(getStockItem(id));
    }
  };

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    if (stockItem) {
      const model = { ...stockItem } as unknown as ChangeStockItemModel;
      model.price = model.price?.value as any;
      model.number = stockItem.product.number;
      reset(model);

      setFile(stockItem.file);
    }
  }, [stockItem]);

  const theme = createTheme({
    components: {
      MuiToolbar: {
        styleOverrides: {
          root: {
            background: "#fff",
            borderBottom: "1px solid #d5d6d9",
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            boderRadius: "6px 0 0 6px !important",
          },
        },
      },
    },
  });

  const { translate: tInventory } = useTranslation("inventory");
  const { translate: tCommon } = useTranslation("common");

  if (loading) {
    return <Loader />;
  }

  return (
    <ThemeProvider theme={theme}>
      <Toolbar className="flex items-center justify-between w-full">
        <div className="flex items-center">
          <p className="heading-2">{tInventory("productDetails")}</p>
        </div>
        <div className="flex"></div>
      </Toolbar>

      <Box className="grid grid-cols-4 gap-5 p-5">
        <Box className="col-span-2">
          <form id="changeStockItemForm" onSubmit={handleSubmit(onSubmit)}>
            <div>
              <InputLabel required>{tCommon("brand")}</InputLabel>
              <Select
                className="w-full"
                defaultValue={stockItem?.product?.manufacturer?.id || ""}
                {...register("manufacturerId", { required: tCommon("forms.required") })}
              >
                {manufacturers.map((manufacturer) => (
                  <MenuItem key={manufacturer.id} value={manufacturer.id}>
                    {manufacturer.name}
                  </MenuItem>
                ))}
              </Select>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-4">
              <div>
                <InputLabel required>{tInventory("productNumber")}</InputLabel>
                <TextField
                  className="w-full"
                  {...register("number", { required: tCommon("forms.required") })}
                  error={!!errors.number}
                  helperText={errors.number?.message}
                />
              </div>

              <div>
                <InputLabel>{tInventory("alternativeNumber")}</InputLabel>
                <TextField
                  className="w-full"
                  {...register("alternativeNumber")}
                  error={!!errors.alternativeNumber}
                  helperText={errors.alternativeNumber?.message}
                />
              </div>
            </div>

            <div className="mt-4">
              <InputLabel>{tCommon("condition")}</InputLabel>
              <RadioGroup row defaultValue={stockItem?.conditionCode}>
                {enumToArray<ProductConditionCode>(ProductConditionCode).map((code) => (
                  <FormControlLabel
                    label={tCommon(`productCondition.${productCondition(code)}`)}
                    value={code}
                    control={<Radio sx={{ color: "#ACAEB3" }} {...register("conditionCode")} />}
                    sx={{ marginRight: 5 }}
                  />
                ))}
              </RadioGroup>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-4">
              <div>
                <InputLabel required>{tCommon("priceEuro")}</InputLabel>
                <TextField
                  className="w-full"
                  {...register("price", { pattern: { value: twoDecimalsRegex, message: tCommon("forms.invalidValue") } })}
                  error={!!errors.price}
                  helperText={errors.price?.message}
                />
              </div>

              <div>
                <InputLabel required>{tInventory("quantity")}</InputLabel>
                <TextField
                  className="w-full"
                  {...register("quantity", {
                    required: tCommon("forms.required"),
                    pattern: { value: integerRegex, message: tCommon("forms.invalidValue") },
                  })}
                  error={!!errors.quantity}
                  helperText={errors.quantity?.message}
                />
              </div>
            </div>

            <div className="mt-4">
              <InputLabel required>{tInventory("category")}</InputLabel>
              <Select
                className="w-full"
                defaultValue={stockItem?.product?.category?.id || ""}
                {...register("categoryId", { required: tCommon("forms.required") })}
              >
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-4">
              <div>
                <InputLabel required>{tInventory("availability")}</InputLabel>
                <Select
                  className="w-full"
                  defaultValue={stockItem?.availabilityCode || ProductAvailabilityCode.inStock}
                  {...register("availabilityCode")}
                >
                  {enumToArray<ProductAvailabilityCode>(ProductAvailabilityCode).map((code) => (
                    <MenuItem key={code} value={code}>
                      {productAvailability(code, tInventory as (key: string, options?: any) => string)}
                    </MenuItem>
                  ))}
                </Select>
              </div>

              <div>
                <InputLabel>{tInventory("weightKg")}</InputLabel>
                <TextField
                  className="w-full"
                  {...register("weight", { pattern: { value: threeDecimalsRegex, message: tCommon("forms.invalidValue") } })}
                  error={!!errors.weight}
                  helperText={errors.weight?.message}
                />
              </div>
            </div>

            <div className="mt-4 grid grid-cols-3 gap-4">
              <div>
                <InputLabel>{tInventory("heightMm")}</InputLabel>
                <TextField
                  className="w-full"
                  {...register("height", { pattern: { value: integerRegex, message: tCommon("forms.invalidValue") } })}
                  error={!!errors.height}
                  helperText={errors.height?.message}
                />
              </div>

              <div>
                <InputLabel>{tInventory("widthMm")}</InputLabel>
                <TextField
                  className="w-full"
                  {...register("width", { pattern: { value: integerRegex, message: tCommon("forms.invalidValue") } })}
                  error={!!errors.width}
                  helperText={errors.width?.message}
                />
              </div>

              <div>
                <InputLabel>{tInventory("depthMm")}</InputLabel>
                <TextField
                  className="w-full"
                  {...register("depth", { pattern: { value: integerRegex, message: tCommon("forms.invalidValue") } })}
                  error={!!errors.depth}
                  helperText={errors.depth?.message}
                />
              </div>
            </div>

            <div className="mt-4">
              <InputLabel>{tInventory("productDescription")}</InputLabel>
              <TextField
                className="w-full textarea"
                multiline
                rows={3}
                {...register("description")}
                error={!!errors.description}
                helperText={errors.description?.message}
              />
            </div>

            <div className="mt-4">
              <InputLabel>{tCommon("uploadFile.image")}</InputLabel>
              <UploadFile file={file} variant="image" onChangeFile={handlerCreateFile} />
            </div>
          </form>
        </Box>

        <Box className="pt-5">
          <Button form="changeStockItemForm" type="submit" variant="contained" className="w-full">
            {tCommon("buttons.save")}
          </Button>

          <Box className="mt-3">
            <Button onClick={handleCancel} variant="outlined" className="w-full mt-3">
              {tCommon("buttons.cancel")}
            </Button>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default EditInventory;
